import { Trans } from '@lingui/macro'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tooltip
} from '@mui/material'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { useFormikContext } from 'formik'
import { cloneDeep } from 'lodash'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { hasEmptyValues } from 'utils'
import ConditionalElementEditor from './ConditionalElementEditor'
import ConfigureMultilanguageTextField from 'app/views/internal/ConfigureMultilanguageTextField'

export const SectionActions = ({ section, index, supportedFormType }) => {
  const dispatch = useDispatch()
  const tree = useSelector((state) => state.formEditorTree)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const [sectionTitleDialogOpen, setSectionTitleDialogOpen] =
    React.useState(false)

  const [sectionPDFEditDialogOpen, setSectionPDFEditDialogOpen] =
    React.useState(false)
  const isPdf =
    supportedFormType.includes('pdf') ||
    supportedFormType.includes('fillable-pdf')

  return (
    <>
      <Dialog open={dialogOpen} maxWidth='pu2' fullWidth>
        <DialogTitleWithIconClose
          label={<Trans>Section conditions</Trans>}
          handleClose={() => setDialogOpen(false)}
        />
        <DialogContent sx={{ padding: '0 48px 48px 48px' }}>
          <ConditionalElementEditor
            conditions={section.conditions}
            depth={String(index)}
            elementType='section'
          />
        </DialogContent>
      </Dialog>
      <EditSectionTitle
        tree={tree}
        sectionIndex={index}
        open={sectionTitleDialogOpen}
        handleClose={(e) => {
          setAnchorEl(null)
          setSectionTitleDialogOpen(false)
        }}
      />
      <SetPdfPropsForSection
        tree={tree}
        sectionIndex={index}
        open={sectionPDFEditDialogOpen}
        handleClose={(e) => {
          setAnchorEl(null)
          setSectionPDFEditDialogOpen(false)
        }}
      />
      <IconButton
        onClick={(e) => {
          e.stopPropagation()
          setAnchorEl(e.target)
        }}
        size='large'
      >
        <Icon fontSize='small'>menu</Icon>
      </IconButton>
      <Menu
        id='simple-menu'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => {
          setAnchorEl(null)
        }}
      >
        <Tooltip title={<Trans>Change section name</Trans>}>
          <MenuItem
            onClick={(e) => {
              setSectionTitleDialogOpen(true)
            }}
          >
            <Icon fontSize='small' style={{ padding: 12, color: '#757575' }}>
              edit
            </Icon>
          </MenuItem>
        </Tooltip>
        <Tooltip title={<Trans>Copy</Trans>}>
          <MenuItem
            onClick={() => {
              setAnchorEl(null)
              dispatch({
                object: { ...cloneDeep(section), section: true },
                type: 'COPY'
              })
            }}
          >
            <Icon fontSize='small' style={{ padding: 12, color: '#757575' }}>
              content_copy
            </Icon>
          </MenuItem>
        </Tooltip>
        <Tooltip title={<Trans>Configure section conditions</Trans>}>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation()
              setAnchorEl(null)
              setDialogOpen(true)
            }}
          >
            <Icon fontSize='small' style={{ padding: 12, color: '#757575' }}>
              flag
            </Icon>
          </MenuItem>
        </Tooltip>

        {isPdf && (
          <Tooltip title={<Trans>SECTION_ACTIONS_SET_PDF_PROPS_TITLE</Trans>}>
            <MenuItem
              onClick={(e) => {
                e.stopPropagation()
                setAnchorEl(null)
                setSectionPDFEditDialogOpen(true)
              }}
            >
              <Icon fontSize='small' style={{ padding: 12, color: '#757575' }}>
                picture_as_pdf
              </Icon>
            </MenuItem>
          </Tooltip>
        )}

        <DeleteSectionButton
          index={index}
          handleClose={() => {
            setAnchorEl(null)
          }}
        />
      </Menu>
    </>
  )
}

const DeleteSectionButton = ({ handleClose, index }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const dispatch = useDispatch()

  return (
    <>
      <Dialog fullWidth maxWidth='xs' open={dialogOpen}>
        <DialogTitleWithIconClose
          label={<Trans>Are you sure you want to delete that section?</Trans>}
          handleClose={() => setDialogOpen(false)}
        />
        <DialogActions sx={{ padding: '0 48px 48px 48px' }}>
          <Grid container direction='column' spacing={2}>
            <Grid item sx={{ width: '100%' }}>
              <Button
                fullWidth
                color='primary'
                variant='outlined'
                onClick={(e) => {
                  e.stopPropagation()
                  setDialogOpen(false)
                }}
              >
                <Trans>Cancel</Trans>
              </Button>
            </Grid>
            <Grid item sx={{ width: '100%' }}>
              <Button
                fullWidth
                color='primary'
                variant='contained'
                onClick={(e) => {
                  e.stopPropagation()
                  setDialogOpen(false)
                  // deleteSection(index)
                  dispatch({
                    deleteIndex: index,
                    type: 'REMOVE_SECTION'
                  })
                  handleClose()
                }}
              >
                <Trans>Confirm</Trans>
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <Tooltip title={<Trans>Delete</Trans>}>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation()
            setDialogOpen(true)
          }}
        >
          <Icon fontSize='small' style={{ padding: 12, color: '#757575' }}>
            delete
          </Icon>
        </MenuItem>
      </Tooltip>
    </>
  )
}

const EditSectionTitle = ({ open, sectionIndex, handleClose, tree }) => {
  const dispatch = useDispatch()

  const { values, setFieldValue } = useFormikContext()

  useEffect(() => {
    if (open) {
      setFieldValue('newSectionTitle', { ...tree.sections[sectionIndex]?.title })
    }
  }, [open, sectionIndex, tree, setFieldValue])

  return (
    <Dialog fullWidth maxWidth='pu2' open={open}>
      <DialogTitleWithIconClose
        label={<Trans>Edit section title</Trans>}
        handleClose={() => handleClose()}
      />
      <DialogContent sx={{ padding: '0 48px 32px 48px' }}>
        <ConfigureMultilanguageTextField
          value={values.newSectionTitle}
          label={<Trans>Section title</Trans>}
          resetToExisting
          values={values.newSectionTitle}
          handleChange={(value) => {
            setFieldValue('newSectionTitle', value)
          }}
        />
      </DialogContent>
      <DialogActions sx={{ padding: '0 48px 48px 48px' }}>
        <Grid container direction='column' spacing={2}>
          <Grid item sx={{ width: '100%' }}>
            <Button
              fullWidth
              color='primary'
              variant='outlined'
              onClick={(e) => {
                e.stopPropagation()
                handleClose()
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
          </Grid>
          <Grid item sx={{ width: '100%' }}>
            <Button
              fullWidth
              color='primary'
              variant='contained'
              onClick={(e) => {
                e.stopPropagation()
                dispatch({
                  type: 'FIELD',
                  depth: [sectionIndex],
                  fieldArray: [
                    {
                      fieldName: 'title',
                      fieldValue: values.newSectionTitle
                    }
                  ]
                })
                handleClose()
              }}
              disabled={!values?.newSectionTitle?.portalTranslationId && hasEmptyValues(values.newSectionTitle, ['en'])}
            >
              <Trans>Confirm</Trans>
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

const SetPdfPropsForSection = ({ open, sectionIndex, handleClose, tree }) => {
  const initialOrientation = tree.sections[sectionIndex]?.orientation
  const [orientation, setOrientation] = React.useState(initialOrientation)
  const dispatch = useDispatch()

  const handleChange = (event) => {
    setOrientation(event.target.value)
  }

  return (
    <Dialog fullWidth open={open} maxWidth='xs'>
      <DialogTitleWithIconClose
        label={<Trans>SECTION_ACTIONS_SET_PDF_PROPS_TITLE</Trans>}
        handleClose={() => handleClose()}
      />
      <DialogContent sx={{ padding: '0 48px 32px 48px' }}>
        <FormControl fullWidth>
          <FormTopLabel id='orientation-select-label'>
            <Trans>SECTION_ACTIONS_ORIENTATION_DROPDOWN_LABEL</Trans>
          </FormTopLabel>
          <Select
            labelId='orientation-select-label'
            value={orientation || 'portrait'}
            onChange={handleChange}
          >
            <MenuItem value='landscape'>
              <Trans>SECTION_ACTIONS_LANDSCAPE_OPTION</Trans>
            </MenuItem>
            <MenuItem value='portrait'>
              <Trans>SECTION_ACTIONS_PORTRAIT_OPTION</Trans>
            </MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ padding: '0 48px 48px 48px' }}>
        <Grid container direction='column' spacing={2}>
          <Grid item sx={{ width: '100%' }}>
            <Button
              fullWidth
              color='primary'
              variant='outlined'
              onClick={(e) => {
                e.stopPropagation()
                handleClose()
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
          </Grid>
          <Grid item sx={{ width: '100%' }}>
            <Button
              fullWidth
              color='primary'
              variant='contained'
              onClick={(e) => {
                e.stopPropagation()
                dispatch({
                  type: 'SET_SECTIONS',
                  sections: [
                    ...tree.sections.slice(0, sectionIndex),
                    {
                      ...tree.sections[sectionIndex],
                      orientation
                    },
                    ...tree.sections.slice(sectionIndex + 1)
                  ]
                })
                handleClose()
              }}
            >
              <Trans>Confirm</Trans>
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}
